import { Service, computed, inject, signal } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { map, tap } from 'rxjs';

import { environment } from '@environments/environment';
import { inlineFeedbackContext } from '@interceptors/snackbar-context';
import { StorageCase } from '@utilities/coreStrings';
import { WRITE_ROLES } from '@enums/role.enum';
import { PortalUser } from '@models/portal-user';
import { ApiResponse } from '@dto/response/page';
import { LoginRequest } from '@dto/request/login-request';
import { PortalRequestPasswordResetRequest } from '@dto/request/portal-request-password-reset-request';
import { ProcessPasswordResetRequest } from '@dto/request/process-password-reset-request';

const INACTIVITY_LIMIT_MS = 15 * 60 * 1000;

const MAX_TIMEOUT_MS = 2_147_483_647;

@Service()
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  private readonly apiUrl = environment.apiUrl;

  private readonly currentUser = signal<PortalUser | null>(readStoredUser());
  private readonly currentToken = signal<string | null>(readStoredToken());

  readonly user = this.currentUser.asReadonly();

  readonly isSignedIn = computed(() => this.currentUser() !== null && this.currentToken() !== null);

  readonly roles = computed(() => this.currentUser()?.roles?.map((role) => role.id) ?? []);

  readonly isReadOnly = computed(() => {
    if (!WRITE_ROLES.length) return false;
    return this.isSignedIn() && !this.roles().some((role) => WRITE_ROLES.includes(role));
  });

  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private expiryTimer: ReturnType<typeof setTimeout> | null = null;
  private activityListeners: AbortController | null = null;

  constructor() {
    if (this.isSignedIn()) this.startSession();
  }

  login(request: LoginRequest) {
    return this.http
      .post<ApiResponse<PortalUser>>(`${this.apiUrl}/admin/auth/login`, request, {
        observe: 'response',
        context: inlineFeedbackContext(),
      })
      .pipe(
        tap((response) => {
          const user = extractUser(response);
          const token = extractToken(response);

          if (user && token) {
            this.setSession(user, token);
            this.startSession();
          }
        }),
        map((response) => response.body),
      );
  }

  logout(): void {
    this.endSession();
    this.currentUser.set(null);
    this.currentToken.set(null);
    void this.router.navigate(['/auth/login']);
  }

  private setSession(user: PortalUser, rawToken: string): void {
    const token = rawToken.startsWith('Bearer ') ? rawToken.slice(7) : rawToken;

    this.currentUser.set(user);
    this.currentToken.set(token);

    try {
      localStorage.setItem(StorageCase.currentUser, JSON.stringify(user));
      localStorage.setItem(StorageCase.token, token);
    } catch {}
  }

  private startSession(): void {
    this.watchActivity();
    this.resetIdleTimer();
    this.scheduleTokenExpiry();
  }

  private endSession(): void {
    this.activityListeners?.abort();
    this.activityListeners = null;

    if (this.idleTimer) clearTimeout(this.idleTimer);
    if (this.expiryTimer) clearTimeout(this.expiryTimer);
    this.idleTimer = null;
    this.expiryTimer = null;

    try {
      localStorage.removeItem(StorageCase.currentUser);
      localStorage.removeItem(StorageCase.token);
      localStorage.removeItem(StorageCase.passwordResetToken);
    } catch {}
  }

  private watchActivity(): void {
    if (this.activityListeners) return;

    const controller = new AbortController();
    this.activityListeners = controller;

    for (const event of ['mousemove', 'keydown', 'click', 'scroll'] as const) {
      window.addEventListener(event, () => this.resetIdleTimer(), {
        signal: controller.signal,
        passive: true,
      });
    }
  }

  private resetIdleTimer(): void {
    if (this.idleTimer) clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(() => this.logout(), INACTIVITY_LIMIT_MS);
  }

  private scheduleTokenExpiry(): void {
    if (this.expiryTimer) clearTimeout(this.expiryTimer);

    const expiresAt = this.tokenExpiry();
    if (expiresAt === null) return;

    const remaining = expiresAt - Date.now();
    if (remaining <= 0) {
      this.logout();
      return;
    }

    if (remaining > MAX_TIMEOUT_MS) {
      this.expiryTimer = setTimeout(() => this.scheduleTokenExpiry(), MAX_TIMEOUT_MS);
      return;
    }

    this.expiryTimer = setTimeout(() => this.logout(), remaining);
  }

  private tokenExpiry(): number | null {
    const token = this.currentToken();
    const payload = token?.split('.')[1];
    if (!payload) return null;

    try {
      const decoded: unknown = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      const exp = (decoded as { exp?: unknown })?.exp;
      return typeof exp === 'number' ? exp * 1000 : null;
    } catch {
      return null;
    }
  }

  getToken(): string | null {
    return this.currentToken();
  }

  getCurrentUser(): PortalUser | null {
    return this.currentUser();
  }

  isAuthenticated(): boolean {
    return this.isSignedIn();
  }

  hasRole(role: string): boolean {
    return this.roles().includes(role);
  }

  hasAnyRole(roles: readonly string[]): boolean {
    if (!roles.length) return true;
    return this.roles().some((role) => roles.includes(role));
  }

  hasWriteAccess(): boolean {
    return !this.isReadOnly();
  }

  requestPasswordReset(request: PortalRequestPasswordResetRequest) {
    return this.http.post<ApiResponse<string>>(
      `${this.apiUrl}/admin/auth/request-password-reset`,
      request,
      {
        context: inlineFeedbackContext(),
      },
    );
  }

  processPasswordReset(request: ProcessPasswordResetRequest) {
    return this.http.post<ApiResponse<string>>(
      `${this.apiUrl}/admin/auth/process-password-reset`,
      request,
      {
        context: inlineFeedbackContext(),
      },
    );
  }
}

function extractToken(response: HttpResponse<ApiResponse<PortalUser>>): string | null {
  const header = response.headers.get('authorization') ?? response.headers.get('Authorization');
  if (header) return header;

  const body = response.body as Record<string, unknown> | null;
  if (!body) return null;

  for (const key of ['token', 'accessToken', 'access_token', 'jwt'] as const) {
    const value = body[key];
    if (typeof value === 'string' && value) return value;
  }

  const inner = body['responseBody'];
  if (typeof inner === 'string' && inner.split('.').length === 3) return inner;

  if (typeof inner === 'object' && inner !== null) {
    const record = inner as Record<string, unknown>;
    for (const key of ['token', 'accessToken', 'access_token', 'jwt'] as const) {
      const value = record[key];
      if (typeof value === 'string' && value) return value;
    }
  }

  return null;
}

function extractUser(response: HttpResponse<ApiResponse<PortalUser>>): PortalUser | null {
  const body = response.body as Record<string, unknown> | null;
  const inner = body?.['responseBody'];

  if (typeof inner === 'object' && inner !== null && 'emailAddress' in inner) {
    return inner as PortalUser;
  }

  const token = extractToken(response);
  return token ? userFromToken(token) : null;
}

function userFromToken(rawToken: string): PortalUser | null {
  const token = rawToken.startsWith('Bearer ') ? rawToken.slice(7) : rawToken;
  const payload = token.split('.')[1];
  if (!payload) return null;

  try {
    const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as Record<
      string,
      unknown
    >;

    const email =
      typeof claims['sub'] === 'string'
        ? claims['sub']
        : typeof claims['emailAddress'] === 'string'
          ? claims['emailAddress']
          : '';
    if (!email) return null;

    const rawRoles = claims['roles'] ?? claims['authorities'];
    const roles = Array.isArray(rawRoles)
      ? rawRoles
          .map((role) =>
            typeof role === 'string'
              ? role
              : ((role as { id?: unknown; authority?: unknown }).id ??
                (role as { authority?: unknown }).authority),
          )
          .filter((role): role is string => typeof role === 'string')
          .map((id) => ({ id, description: id }))
      : [];

    return {
      id: typeof claims['id'] === 'number' ? claims['id'] : 0,
      firstName: typeof claims['firstName'] === 'string' ? claims['firstName'] : email,
      lastName: typeof claims['lastName'] === 'string' ? claims['lastName'] : '',
      nationalIdNumber: '',
      emailAddress: email,
      password: '',
      profileImageUrl: '',
      isEnabled: true,
      isAccountNonExpired: true,
      isAccountNonLocked: true,
      isCredentialsNonExpired: true,
      dateCreated: '',
      dateUpdated: '',
      roles,
      deletedAt: '',
    };
  } catch {
    return null;
  }
}

function readStoredUser(): PortalUser | null {
  try {
    const raw = localStorage.getItem(StorageCase.currentUser);
    return raw ? (JSON.parse(raw) as PortalUser) : null;
  } catch {
    return null;
  }
}

function readStoredToken(): string | null {
  try {
    return localStorage.getItem(StorageCase.token);
  } catch {
    return null;
  }
}
